import { atom } from 'nanostores';
import { logger } from '@nanostores/logger';
import { $bundleItems } from '../../src/js/bundle-store';

// Get theme settings for maxBoxSize, bundleSize & packDiscounts
const maxBoxSize = globalThis.PxlApp.maxBoxSize;
const bundleSize = globalThis.PxlApp.bundleSize;
const packDiscounts = globalThis.PxlApp.packDiscounts;

/**  Transform packDiscounts into usable
 * The packDiscount is in the form 6:5, 12:7, 24:10
 * We transform it into the form [{quantity: 6, rate: 5}, {quantity: 12, rate: 7}, {quantity: 24, rate: 10}]
 **/
const discounts = packDiscounts.split(',').map(discount => {
  let discountString = discount.trim();
  return {
    quantity: Number.parseInt(discountString.split(':')[0]),
    rate: Number.parseInt(discountString.split(':')[1])
  };
});

// Initiate an atom store for bundleNotifications
export const $bundleNotifications = atom([]);

if (globalThis.PxlApp.isDev) {
  logger({
    $bundleNotifications: $bundleNotifications
  });
}

/**
 * Add a notification to the $bundleNotification store
 *
 * @param {String} type required
 * @param {Integer} number optional
 * @param {String} title options
 * @returns {null}
 */
export function addNotification({ type, number, title }) {
  const id = Date.now();
  let notification = { id: id, type: type };

  if (number) {
    notification.number = number;
  }

  if (title) {
    notification.title = title;
  }

  $bundleNotifications.set([notification, ...$bundleNotifications.get()]);
  let timeout = 3000;

  if (type == 'discount') {
    timeout = 3500;
  }

  hideNotification(notification, timeout);
}

/**
 * Remove a notification from the store after a timeout
 *
 * @param {Object} notification
 * @param {Integer} timeout
 * @returns {null}
 */
function hideNotification(notification, timeout) {
  setTimeout(() => {
    let items = $bundleNotifications.get();
    const index = items.indexOf(notification);
    items.splice(index, 1);
    $bundleNotifications.set([...items]);
  }, timeout);
}

/**
 * Uses the bundleItems state changes to add different notification types
 *
 * @param {Integer} oldCount
 * @param {Integer} newCount
 * @param {Object} newItem optional
 * @returns {null}
 */
export function notifyBundleChanges(oldCount, newCount, newItem) {
  // When the first item is added to the bundle
  if (oldCount == 0) {
    addNotification({ type: 'startedAdding' });
  }

  // When the bundle is completely filled
  if (newCount % bundleSize == 0) {
    const boxCount = Math.ceil(newCount / maxBoxSize);
    addNotification({ type: 'readyToPurchase', number: boxCount });
  }

  // When a first item is added to a completed bundle
  if (oldCount != 0 && newCount % bundleSize == 1) {
    addNotification({ type: 'fillingBiggerBox' });
  }

  let slotsFilled = newCount % bundleSize;

  if (slotsFilled == 0) {
    slotsFilled = bundleSize;
  }

  const emptySlots = bundleSize - slotsFilled;

  // When two or one slot is remaining to be filled in the bundle
  if (emptySlots <= 2 && emptySlots > 0) {
    addNotification({ type: 'itemsLeft', number: emptySlots });
  }

  // When the added variants have quantities where a discount is applied
  if (newItem) {
    const item = $bundleItems
      .get()
      .find(item => item.variantId === newItem.variantId);

    const discount = discounts.find(
      discount => discount.quantity == item.quantity
    );

    if (discount) {
      const percentage = discount.rate;

      addNotification({
        type: 'discount',
        number: percentage,
        title: newItem.title
      });
    }
  }
}

/**
 * Clear the notifications store
 *
 * @returns {null}
 */
export function clearNotifications() {
  $bundleNotifications.set([]);
}
